import type { KanzleilandIntegration } from "@/apis/gql/types";

import { useCreateEmailForwarding } from "@/queries/email-forwardings/create";
import { useDeleteEmailForwarding } from "@/queries/email-forwardings/delete";
import { useGetAllEmailForwardings } from "@/queries/email-forwardings/get-all";
import { useUpdateEmailForwarding } from "@/queries/email-forwardings/update";

import { useGetIntegration } from "@/queries/integrations/get";

import { KanzleilandIntegrations } from "./kanzleiland-integration";

import { useCreateIntegration } from "@/queries/integrations/create";
import { useGetKanzleilandFolders } from "@/queries/integrations/kanzleiland/folders";
import { useUpdateIntegration } from "@/queries/integrations/update";

import { useDeleteIntegration } from "@/queries/integrations/delete";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";

const KANZLEILAND_INTEGRATION_NAME = "kanzleiland";

export const KanzleilandIntegrationsContainer = () => {
  const { integration, loading, error } =
    useGetIntegration<KanzleilandIntegration>({
      integrationName: KANZLEILAND_INTEGRATION_NAME,
    });
  const [createIntegration] = useCreateIntegration();
  const [updateIntegration] = useUpdateIntegration();
  const [deleteIntegration] = useDeleteIntegration();

  const { folders } = useGetKanzleilandFolders({
    enabled: !!integration?.integrationAuth.userToken,
  });

  const handleCreateIntegration = async (
    integrationAuth: KanzleilandIntegration["integrationAuth"],
  ) => {
    await createIntegration({
      integrationName: KANZLEILAND_INTEGRATION_NAME,
      integrationAuth,
    });
  };

  const handleFolderSelectionChange = async (
    selectedFolders: KanzleilandIntegration["integrationConfig"]["folderIds"],
  ) => {
    if (!integration) return;

    await updateIntegration({
      integrationId: integration.integrationId,
      integrationName: KANZLEILAND_INTEGRATION_NAME,
      integrationConfig: {
        ...integration.integrationConfig,
        folderIds: selectedFolders,
      },
    });
  };

  const handleImportDateChange = async (
    importDate: KanzleilandIntegration["integrationConfig"]["importDate"],
  ) => {
    if (!integration) return;

    await updateIntegration({
      integrationId: integration.integrationId,
      integrationName: KANZLEILAND_INTEGRATION_NAME,
      integrationConfig: {
        ...integration.integrationConfig,
        importDate,
      },
    });
  };

  const handleDisconnect = async () => {
    if (!integration) return;

    await deleteIntegration({ integrationId: integration.integrationId });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full p-4">
        <ErrorMessage />
      </div>
    );
  }

  return (
    <KanzleilandIntegrations
      integration={integration}
      loading={loading}
      folders={folders}
      onCreate={handleCreateIntegration}
      onDisconnect={handleDisconnect}
      onFolderSelectionChange={handleFolderSelectionChange}
      onImportDateChange={handleImportDateChange}
    />
  );
};
